import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaDate,
  apaInTextAuthors,
  apaNameInitialsFirst,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaDate,
  mlaInTextAuthors,
  naturalName,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaUrl,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  apaAmpersandList,
  beforeSubtitle,
  chicagoShortTitle,
  longDate,
  type MediaLabels,
  type MediaValues,
  mlaRoleNames,
  mlaShortTitle,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";

/*
 * Comics: graphic novels and collected editions, single comic-book issues, and
 * webcomics or strips read online — in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7 has no comics example. A graphic novel or an issue follows the book format
 *   (§10.2) with the illustrator credited after the title, as APA does for illustrated
 *   books — "Crimi, C. (2019). Weird little robots (C. Luyken, Illus.). Candlewick
 *   Press." — where the illustrations carry the work; the illustrator and an edition
 *   or volume share one parenthesis, separated by a semicolon ("(M. Sendak, Illus.;
 *   50th anniversary ed.)") — APA Style, "Children's book or other illustrated book
 *   references". A bracketed description after the title identifies the format:
 *   "When in doubt, include a description … you may alter the wording" (APA Style,
 *   "Elements of reference list entries", §9.21), hence [Graphic novel], [Comic book],
 *   [Webcomic], [Comic strip]. A webcomic follows the webpage format: italic title,
 *   then the site name and URL.
 * - MLA 9: MLA Style Center, "How do I cite comics or a graphic novel if I want to
 *   credit several collaborators as equal creators of the work?" — "March. By John
 *   Lewis and Andrew Aydin, illustrated by Nate Powell, book 1, Top Shelf
 *   Productions, 2013." — roles described with a phrase ("illustrated by") and "et
 *   al." when more than two share one role; "How do I cite one issue of a comic book?"
 *   — the Number element (vol. 2, no. 24), and a writer-led form for when one creator
 *   is the focus. A webcomic is a work on a website: its title in quotation marks,
 *   the site in italics, the date and the URL.
 * - Chicago 18: a graphic novel or collected edition is cited like a book (§14.30: no
 *   place of publication), with the illustrator after the title; the titles of comic
 *   strips and cartoon series are italic (CMOS 8.202, via the Chicago Style Q&A on
 *   cartoon series). A webcomic follows the Quick Guide's web-page form.
 */

export const COMIC_FIELDS = {
  writers: "writers",
  artists: "artists",
  colorist: "colorist",
  letterer: "letterer",
  title: "title",
  volume: "volume",
  issue: "issue",
  publisher: "publisher",
  date: "date",
  stripTitle: "stripTitle",
  stripKind: "stripKind",
  site: "site",
  url: "url",
  equalCredit: "equalCredit",
} as const;

const F = COMIC_FIELDS;

/** A graphic novel or collected edition, one issue, or a webcomic or strip online. */
export type ComicKind = "book" | "issue" | "web";

export interface ComicLabels extends MediaLabels {
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Comic {
  kind: ComicKind;
  writers: PersonName[];
  artists: PersonName[];
  colorist: string;
  letterer: string;
  title: string;
  volume: string;
  issue: string;
  publisher: string;
  date: PartialDate | null;
  stripTitle: string;
  isStrip: boolean;
  site: string;
  url: string;
  equalCredit: boolean;
}

function readComic(kind: ComicKind, values: MediaValues): Comic {
  return {
    kind,
    writers: parsePeople(get(values, F.writers)),
    artists: parsePeople(get(values, F.artists)),
    colorist: kind === "web" ? "" : get(values, F.colorist),
    letterer: kind === "web" ? "" : get(values, F.letterer),
    title: capitalizeSubtitle(get(values, F.title)),
    volume: kind === "web" ? "" : get(values, F.volume),
    issue: kind === "issue" ? get(values, F.issue) : "",
    publisher: kind === "web" ? "" : get(values, F.publisher),
    date: parsePartialDate(get(values, F.date)),
    stripTitle:
      kind === "web" ? capitalizeSubtitle(get(values, F.stripTitle)) : "",
    isStrip: get(values, F.stripKind) === "strip",
    site: kind === "web" ? get(values, F.site) : "",
    url: get(values, F.url),
    equalCredit: get(values, F.equalCredit) === "true",
  };
}

const RE_DIGITS = /^\d+$/;
const RE_ENDS_IN_MARK = /[?!]$/;

/** "2" → "vol. 2"; "Book 1" → "book 1" — MLA's and Chicago's lowercase label. */
function volumeLabel(volume: string): string {
  if (volume === "") {
    return "";
  }
  if (RE_DIGITS.test(volume)) {
    return `vol. ${volume}`;
  }
  return volume.charAt(0).toLowerCase() + volume.slice(1);
}

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function quotedTitle(title: string, mark: "," | "."): string {
  return RE_ENDS_IN_MARK.test(title) ? `“${title}”` : `“${title}${mark}”`;
}

/** The title readers look for: a webcomic's own title, else the series or book. */
function workTitle(comic: Comic): string {
  return comic.kind === "web" && comic.stripTitle
    ? comic.stripTitle
    : comic.title;
}

/**
 * The series, book or webcomic name in title case — except a webcomic's or site's
 * name, which keeps its own styling ("xkcd").
 */
function seriesName(comic: Comic): string {
  return comic.kind === "web" ? comic.title : titleCase(comic.title);
}

/** The cited title in title case, for in-text citations and shortened notes. */
function titleForText(comic: Comic): string {
  if (comic.kind === "web" && comic.stripTitle) {
    return titleCase(comic.stripTitle);
  }
  return seriesName(comic);
}

/** Who leads the entry: the writers, or the artists when no writer is named. */
function leadCreators(comic: Comic): PersonName[] {
  return comic.writers.length > 0 ? comic.writers : comic.artists;
}

/** Artists are credited separately only when writers lead the entry. */
function creditedArtists(comic: Comic): PersonName[] {
  return comic.writers.length > 0 ? comic.artists : [];
}

/* ---------------------------------------------------------------------- APA */

function apaLabel(comic: Comic): string {
  if (comic.kind === "book") {
    return "Graphic novel";
  }
  if (comic.kind === "issue") {
    return "Comic book";
  }
  return comic.isStrip ? "Comic strip" : "Webcomic";
}

/** "(N. Powell, Illus.; Book 1)", "(F. Staples, Illus.; No. 1)" or "". */
function apaCredits(comic: Comic): string {
  const pieces: string[] = [];
  const artists = creditedArtists(comic);
  if (artists.length > 0) {
    pieces.push(
      `${apaAmpersandList(artists.map(apaNameInitialsFirst))}, Illus.`
    );
  }
  if (comic.volume) {
    pieces.push(
      RE_DIGITS.test(comic.volume)
        ? `Vol. ${comic.volume}`
        : capitalizeFirst(comic.volume)
    );
  }
  if (comic.issue) {
    pieces.push(`No. ${comic.issue}`);
  }
  return pieces.length > 0 ? ` (${pieces.join("; ")})` : "";
}

function apaSource(comic: Comic): string {
  const name =
    comic.kind === "web" ? comic.site || comic.title : comic.publisher;
  if (comic.kind === "web" && name === workTitle(comic)) {
    return comic.url;
  }
  return [name ? `${name}${closer(name)}` : "", comic.url]
    .filter(Boolean)
    .join(" ");
}

function apaReference(comic: Comic): CitationParts {
  const people = leadCreators(comic);
  const date = `(${apaDate(comic.date)}).`;
  const after = `${apaCredits(comic)} [${apaLabel(comic)}].`;
  const source = apaSource(comic);
  if (people.length === 0) {
    return tidyParts([
      italic(workTitle(comic)),
      plain(`${after} ${date} ${source}`),
    ]);
  }
  const authors = apaAuthors(people);
  return tidyParts([
    plain(`${authors}${closer(authors)} ${date} `),
    italic(workTitle(comic)),
    plain(`${after} ${source}`),
  ]);
}

function apaOutput(comic: Comic, labels: ComicLabels): CitationOutput {
  const people = leadCreators(comic);
  const year = comic.date ? String(comic.date.year) : "n.d.";
  const short = beforeSubtitle(titleForText(comic));
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(comic) },
      {
        label: labels.parenthetical,
        parts:
          people.length > 0
            ? [plain(`(${apaInTextAuthors(people)}, ${year})`)]
            : [plain("("), italic(short), plain(`, ${year})`)],
      },
      {
        label: labels.narrative,
        parts:
          people.length > 0
            ? [plain(`${apaInTextAuthors(people, true)} (${year})`)]
            : [italic(short), plain(` (${year})`)],
      },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

/**
 * "illustrated by Nate Powell", "colored by …", "lettered by …". An artist who is also
 * a writer is not named twice — MLA and Chicago credit a cartoonist once (APA, by its
 * illustrated-book rule, does credit the same person twice).
 */
function roleCredits(comic: Comic): string[] {
  const credits: string[] = [];
  const writers = new Set(comic.writers.map(naturalName));
  const artists = creditedArtists(comic).filter(
    (artist) => !writers.has(naturalName(artist))
  );
  if (artists.length > 0) {
    credits.push(`illustrated by ${mlaRoleNames(artists.map(naturalName))}`);
  }
  if (comic.colorist) {
    credits.push(`colored by ${comic.colorist}`);
  }
  if (comic.letterer) {
    credits.push(`lettered by ${comic.letterer}`);
  }
  return credits;
}

function mlaPrintEntry(comic: Comic): CitationParts {
  const title = titleCase(comic.title);
  const leadByTitle = comic.equalCredit && comic.writers.length > 0;
  const elements = [
    leadByTitle ? `by ${mlaRoleNames(comic.writers.map(naturalName))}` : "",
    ...roleCredits(comic),
    volumeLabel(comic.volume),
    comic.issue ? `no. ${comic.issue}` : "",
    comic.publisher,
    mlaDate(comic.date),
    comic.url ? mlaUrl(comic.url) : "",
  ].filter(Boolean);
  const parts: CitationParts = [];
  const people = leadCreators(comic);
  if (!leadByTitle && people.length > 0) {
    const authors = mlaAuthors(people);
    parts.push(plain(`${authors}${closer(authors)} `));
  }
  parts.push(italic(title), plain(closer(title)));
  if (elements.length > 0) {
    parts.push(plain(` ${withPeriod(capitalizeFirst(elements.join(", ")))}`));
  }
  return tidyParts(parts);
}

function mlaWebEntry(comic: Comic): CitationParts {
  const parts: CitationParts = [];
  const people = leadCreators(comic);
  if (people.length > 0) {
    const authors = mlaAuthors(people);
    parts.push(plain(`${authors}${closer(authors)} `));
  }
  const series = seriesName(comic);
  const container = comic.site || series;
  const details = [mlaDate(comic.date), comic.url ? mlaUrl(comic.url) : ""]
    .filter(Boolean)
    .join(", ");
  if (comic.stripTitle) {
    parts.push(plain(`${quotedTitle(titleCase(comic.stripTitle), ".")} `));
  } else if (comic.site) {
    parts.push(italic(series), plain(`${closer(series)} `));
  }
  parts.push(italic(container), plain(details ? `, ${details}.` : "."));
  return tidyParts(parts);
}

function mlaInText(comic: Comic): CitationParts {
  const people = leadCreators(comic);
  const leadByTitle = comic.equalCredit && comic.kind !== "web";
  if (people.length > 0 && !leadByTitle) {
    return [plain(`(${mlaInTextAuthors(people)})`)];
  }
  if (comic.kind === "web" && comic.stripTitle) {
    return [plain(`(“${beforeSubtitle(titleCase(comic.stripTitle))}”)`)];
  }
  return [plain("("), italic(mlaShortTitle(seriesName(comic))), plain(")")];
}

function mlaOutput(comic: Comic, labels: ComicLabels): CitationOutput {
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      {
        label: labels.worksCited,
        parts: comic.kind === "web" ? mlaWebEntry(comic) : mlaPrintEntry(comic),
      },
      { label: labels.inText, parts: mlaInText(comic) },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoPrintNote(comic: Comic): CitationParts {
  const people = leadCreators(comic);
  const credits = [
    ...roleCredits(comic),
    volumeLabel(comic.volume),
    comic.issue ? `no. ${comic.issue}` : "",
  ].filter(Boolean);
  const facts = [comic.publisher, longDate(comic.date) || "n.d."].filter(
    Boolean
  );
  const parts: CitationParts = [];
  if (people.length > 0) {
    parts.push(plain(`${chicagoNoteAuthors(people)}, `));
  }
  parts.push(italic(titleCase(comic.title)));
  const middle = credits.length > 0 ? `, ${credits.join(", ")}` : "";
  const url = comic.url ? `, ${comic.url}` : "";
  parts.push(plain(`${middle} (${facts.join(", ")})${url}.`));
  return tidyParts(parts);
}

function chicagoPrintBibliography(comic: Comic): CitationParts {
  const people = leadCreators(comic);
  const title = titleCase(comic.title);
  const parts: CitationParts = [];
  if (people.length > 0) {
    parts.push(plain(`${withPeriod(chicagoBibliographyAuthors(people))} `));
  }
  parts.push(italic(title), plain(closer(title)));
  const sentences: string[] = [];
  const credits = roleCredits(comic);
  if (credits.length > 0) {
    sentences.push(withPeriod(capitalizeFirst(credits.join(", "))));
  }
  const numbering = [
    volumeLabel(comic.volume),
    comic.issue ? `no. ${comic.issue}` : "",
  ].filter(Boolean);
  if (numbering.length > 0) {
    sentences.push(withPeriod(capitalizeFirst(numbering.join(", "))));
  }
  const facts = [comic.publisher, longDate(comic.date) || "n.d."].filter(
    Boolean
  );
  sentences.push(withPeriod(facts.join(", ")));
  if (comic.url) {
    sentences.push(`${comic.url}.`);
  }
  parts.push(plain(` ${sentences.join(" ")}`));
  return tidyParts(parts);
}

function chicagoWebParts(
  comic: Comic,
  closing: "note" | "bibliography"
): CitationParts {
  const people = leadCreators(comic);
  const series = seriesName(comic);
  const container = comic.site || series;
  const note = closing === "note";
  const parts: CitationParts = [];
  if (people.length > 0) {
    const names = note
      ? `${chicagoNoteAuthors(people)}, `
      : `${withPeriod(chicagoBibliographyAuthors(people))} `;
    parts.push(plain(names));
  }
  if (comic.stripTitle) {
    parts.push(
      plain(`${quotedTitle(titleCase(comic.stripTitle), note ? "," : ".")} `)
    );
  } else if (comic.site) {
    parts.push(italic(series), plain(note ? ", " : ". "));
  }
  parts.push(italic(container));
  const date = longDate(comic.date);
  if (note) {
    const tail = [date, comic.url].filter(Boolean).join(", ");
    parts.push(plain(tail ? `, ${tail}.` : "."));
  } else {
    parts.push(plain(date ? `, ${date}.` : "."));
    parts.push(plain(comic.url ? ` ${comic.url}.` : ""));
  }
  return tidyParts(parts);
}

function chicagoShortNote(comic: Comic): CitationParts {
  const people = leadCreators(comic);
  const lead = people.length > 0 ? `${chicagoShortAuthors(people)}, ` : "";
  if (comic.kind === "web" && comic.stripTitle) {
    return [
      plain(
        `${lead}${quotedTitle(chicagoShortTitle(titleCase(comic.stripTitle)), ".")}`
      ),
    ];
  }
  return [
    plain(lead),
    italic(chicagoShortTitle(seriesName(comic))),
    plain("."),
  ];
}

function chicagoOutput(comic: Comic, labels: ComicLabels): CitationOutput {
  const isWeb = comic.kind === "web";
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      {
        label: labels.footnote,
        parts: isWeb ? chicagoWebParts(comic, "note") : chicagoPrintNote(comic),
      },
      { label: labels.shortNote, parts: chicagoShortNote(comic) },
      {
        label: labels.bibliography,
        parts: isWeb
          ? chicagoWebParts(comic, "bibliography")
          : chicagoPrintBibliography(comic),
      },
    ],
    note: labels.chicagoNote,
  };
}

export function comicIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatComic(
  kind: ComicKind,
  values: MediaValues,
  labels: ComicLabels
): CitationOutput[] {
  const comic = readComic(kind, values);
  return [
    apaOutput(comic, labels),
    mlaOutput(comic, labels),
    chicagoOutput(comic, labels),
  ];
}
